<template>
  <form @submit.stop.prevent>
    <vue-loading :active="isInitialLoading || actionLoading" :is-full-page="false" />
    <FlexLayout>
      <BlockContainer>
        <p
          v-if="isEditMode && isEditAddressLoaded && form.isPrimary === true"
          class="address-primary-status"
          role="status"
          aria-live="polite"
        >
          {{ $t('addressBook.ariaLabel.isPrimary') }}
        </p>
        <!-- Use dynamic fieldsConfig instead of hardcoded REGISTRATION section -->
        <Input
            v-if="fieldsConfig.firstName && fieldsConfig.firstName.visible"
            id="firstName"
            :label="$t('field.firstName')"
            type="text"
            v-model="$v.form.firstName.$model"
            :error="$v.form.firstName.$dirty && $v.form.firstName.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.firstName.required"
            :maxlength="fieldsConfig.firstName.maxLength"
            aria-describedby="address-form-firstName"
            autofocus
        />
        <Input
            v-if="fieldsConfig.lastName && fieldsConfig.lastName.visible"
            id="lastName"
            :label="$t('field.lastName')"
            type="text"
            v-model="$v.form.lastName.$model"
            :error="$v.form.lastName.$dirty && $v.form.lastName.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.lastName.required"
            :maxlength="fieldsConfig.lastName.maxLength"
            aria-describedby="address-form-lastName"
            autofocus
        />
        <Input
            v-if="fieldsConfig.streetAddress && fieldsConfig.streetAddress.visible"
            id="streetAddress"
            :label="$t('field.street')"
            type="text"
            v-model="$v.form.streetAddress.$model"
            :error="$v.form.streetAddress.$dirty && $v.form.streetAddress.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.streetAddress.required"
            :maxlength="fieldsConfig.streetAddress.maxLength"
            aria-describedby="address-form-streetAddress"
            autofocus
        />
        <Input
            v-if="fieldsConfig.postcode && fieldsConfig.postcode.visible"
            id="postcode"
            :label="$t('field.postalCode')"
            type="text"
            v-model="$v.form.postcode.$model"
            :error="$v.form.postcode.$dirty && $v.form.postcode.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.postcode.required"
            :maxlength="fieldsConfig.postcode.maxLength"
            aria-describedby="address-form-postcode"
            autofocus
        />
        <Input
            v-if="fieldsConfig.city && fieldsConfig.city.visible"
            id="city"
            :label="$t('field.city')"
            type="text"
            v-model="$v.form.city.$model"
            :error="$v.form.city.$dirty && $v.form.city.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.city.required"
            :maxlength="fieldsConfig.city.maxLength"
            aria-describedby="address-form-city"
            autofocus
        />
        <Dropdown v-if="countriesLoaded && fieldsConfig.countryId && fieldsConfig.countryId.visible"
                  v-model="$v.form.countryId.$model"
                  :label="$t('field.country')"
                  :options="countryOptions"
                  :required="fieldsConfig.countryId.required"
        />
        <Input
            v-if="fieldsConfig.telephoneNumber && fieldsConfig.telephoneNumber.visible"
            id="telephoneNumber"
            :label="$t('field.telephoneNumber')"
            type="text"
            v-model="$v.form.telephoneNumber.$model"
            :error="$v.form.telephoneNumber.$dirty && $v.form.telephoneNumber.$error ? $t('validation.fieldWarningRequired') : ''"
            :required="fieldsConfig.telephoneNumber.required"
            :maxlength="fieldsConfig.telephoneNumber.maxLength"
            aria-describedby="address-form-telephoneNumber"
            autofocus
        />
        <template #actions>
          <Button type="submit" @click.stop="onSubmit" :disabled="$v.$invalid" variant="primary">{{ $t('addressBook.button.save') }}</Button>
          <Button
            v-if="isEditMode && isEditAddressLoaded && form.isPrimary === false"
            type="button"
            variant="secondary"
            :disabled="actionLoading"
            @click.stop="showSetPrimaryDialog = true"
          >
            {{ $t('addressBook.button.setPrimary') }}
          </Button>
          <Button
            v-if="isEditMode && isEditAddressLoaded && form.isPrimary === false"
            type="button"
            variant="danger"
            :disabled="actionLoading"
            @click.stop="showDeleteDialog = true"
          >
            {{ $t('addressBook.button.delete') }}
          </Button>
        </template>
      </BlockContainer>

      <ConfirmDialog
        v-model="showSetPrimaryDialog"
        :title="$t('addressBook.dialog.primary.title')"
        :message="$t('addressBook.dialog.primary.message')"
        :okLabel="$t('system.button.yes')"
        :cancelLabel="$t('system.button.no')"
        :okDisabled="actionLoading"
        @confirm="onSetPrimary"
      />

      <ConfirmDialog
        v-model="showDeleteDialog"
        :title="$t('addressBook.dialog.delete.title')"
        :message="$t('addressBook.dialog.delete.message')"
        :okLabel="$t('system.button.yes')"
        :cancelLabel="$t('system.button.no')"
        :okDisabled="actionLoading"
        @confirm="onDeleteAddress"
      />
    </FlexLayout>
  </form>
</template>

<script>
import Input from "@/components/forms/Input.vue";
import Dropdown from "@/components/forms/Dropdown.vue";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import { isRequired } from '@/utils/validationUtils';
import Button from "@/components/actions/Button.vue";
import {addressService} from "@/services/api/addressService";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import {accountService} from "@/services/api/accountService";
import ConfirmDialog from "@/components/feedback/ConfirmDialog.vue";
import {useAppStore} from "@/stores/app";
import VueLoading from "vue-loading-overlay";

export default {
  setup() {
    const appStore = useAppStore();
    return { appStore };
  },
  props: {
    // Mode determines which config section and behavior to use
    mode: {
      type: String,
      default: 'create', // 'create' | 'edit'
    },
    addressId: {
      type: [String, Number],
      default: null
    }
  },
    data() {
        return {
          form: {
            isPrimary: null,
            firstName: undefined,
            lastName: undefined,
            streetAddress: undefined,
            postcode: undefined,
            city: undefined,
            countryId: undefined,
            telephoneNumber: undefined,
          },
          isInitialLoading: false,
          isEditAddressLoaded: false,
          countriesLoaded: false,
          actionLoading: false,
          showSetPrimaryDialog: false,
          showDeleteDialog: false,
          countries: {
            Type: Array
          },
        }
	},
    computed: {
      isEditMode() {
        return this.mode === 'edit' && !!this.addressId;
      },
      fieldsConfig() {
        return window.config.ADDRESS.FIELDS;
      },
      countryOptions() {
        if (!Array.isArray(this.countries)) return [];
        return this.countries.map(c => ({ value: c.id, label: c.name }));
      },
    },
    components:{
      FlexLayout,
      Button,
      ConfirmDialog,
      VueLoading,
      BlockContainer,
      Dropdown: Dropdown,
      Input,
    },
    validations() {
      const fields = this.fieldsConfig || {};
      return {
        form: {
          firstName: { required: v => isRequired(v, fields.firstName) },
          lastName: { required: v => isRequired(v, fields.lastName) },
          streetAddress: { required: v => isRequired(v, fields.streetAddress) },
          postcode: { required: v => isRequired(v, fields.postcode) },
          city: { required: v => isRequired(v, fields.city) },
          countryId: { required: v => isRequired(v, fields.countryId) },
          telephoneNumber: { required: v => isRequired(v, fields.telephoneNumber) },
        }
      }
    },
    methods: {
      async onSubmit() {
        this.$v.$touch();

        if (this.$v.$invalid) {
          this.makeToast('danger', this.$t('notification.fillAllRequiredFields'));
          return;
        }

        this.actionLoading = true;

        let result;
        if (this.mode === 'edit') {
          result = await addressService.editAddress(this.form);
        } else {
          result = await addressService.createAddress(this.form);
        }

        this.actionLoading = false;

        if (result.exception !== undefined) {
          this.makeToast('danger', this.$t('addressBook.messages.error.saveAddress'));
          return;
        }

        this.appStore.setFlashToast({
          variant: 'success',
          message: this.$t('addressBook.messages.info.saveAddress'),
        });
        this.$navigateToAddressBook(false);
      },
      async onSetPrimary() {
        if (!this.addressId) {
          return;
        }

        this.actionLoading = true;
        const addressDetails = await addressService.getById(this.addressId);
        const result = await addressService.setAsPrimary(addressDetails);
        this.actionLoading = false;
        this.showSetPrimaryDialog = false;

        if (result.exception !== undefined) {
          this.makeToast('danger', this.$t('addressBook.messages.error.setPrimaryAddress'));
          return;
        }

        this.appStore.setFlashToast({
          variant: 'success',
          message: this.$t('addressBook.messages.info.setPrimaryAddress'),
        });
        this.$navigateToAddressBook(false);
      },
      async onDeleteAddress() {
        if (!this.addressId) {
          return;
        }

        this.actionLoading = true;
        const result = await addressService.deleteById(this.addressId);
        this.actionLoading = false;
        this.showDeleteDialog = false;

        if (result.exception !== undefined) {
          this.makeToast('danger', this.$t('addressBook.messages.error.deleteAddress'));
          return;
        }

        this.appStore.setFlashToast({
          variant: 'success',
          message: this.$t('addressBook.messages.info.deleteAddress'),
        });
        this.$navigateToAddressBook(false);
      },
      async fillForm() {
        const result = await addressService.getById(this.addressId);

        this.form.isPrimary = result.isPrimary;
        this.form.firstName = result.firstName;
        this.form.lastName = result.lastName;
        this.form.streetAddress = result.streetAddress;
        this.form.postcode = result.postcode;
        this.form.city = result.city;
        this.form.countryId = result.countryId;
        this.form.telephoneNumber = result.telephoneNumber;

        this.countries = result.countries;
        this.countriesLoaded = true;
        this.isEditAddressLoaded = true;
      },
    },
  async created(){
    if (this.mode === 'edit') {
      this.isInitialLoading = true;
      try {
        await this.fillForm();
      } catch (e) {
        this.makeToast('danger', this.$t('addressBook.messages.error.loadAddresses'));
      } finally {
        this.isInitialLoading = false;
      }
    } else if (this.mode === 'create') {
      const result = await accountService.getCountries();
      this.countries = result.countries;
      this.countriesLoaded = true;
    }
  },
}
</script>

<style scoped>
.address-primary-status {
  margin: 0 0 0.75rem;
  font-weight: 700;
}
</style>

